import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import API_BASE_URL from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

// Loan Application Form Component
const LoanApplicationForm: React.FC<{
  isVisible: boolean;
  onClose: () => void;
  onSubmit: (loanData: any) => Promise<void>;
  chamaId: string;
}> = ({ isVisible, onClose, onSubmit, chamaId }) => {
  const colors = getThemeColors('dark');
  const [formData, setFormData] = useState({
    amount: '',
    purpose: '',
    monthlyIncome: '',
    repaymentPeriod: '12',
    interestRate: '5',
    businessPlan: '',
    otherLoans: '',
    guarantors: [] as any[],
  });
  const [availableGuarantors, setAvailableGuarantors] = useState<any[]>([]);
  const [guarantorSearch, setGuarantorSearch] = useState('');
  const [showGuarantorSearch, setShowGuarantorSearch] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadAvailableGuarantors = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/user-search/search?query=${encodeURIComponent(guarantorSearch || 'a')}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Filter out current user and already selected guarantors
          const currentUserId = JSON.parse(atob(authToken!.split('.')[1])).userId;
          const filteredUsers = (data.data || []).filter((user: any) =>
            user.id !== currentUserId &&
            !formData.guarantors.some(g => g.id === user.id)
          );
          setAvailableGuarantors(filteredUsers);
        }
      }
    } catch (error) {
      console.error('Failed to load guarantors:', error);
    }
  };

  useEffect(() => {
    if (showGuarantorSearch && guarantorSearch.length >= 2) {
      loadAvailableGuarantors();
    }
  }, [guarantorSearch, showGuarantorSearch]);

  const addGuarantor = (guarantor: any) => {
    if (formData.guarantors.length < 5) {
      setFormData(prev => ({
        ...prev,
        guarantors: [...prev.guarantors, guarantor]
      }));
      setShowGuarantorSearch(false);
      setGuarantorSearch('');
    }
  };

  const removeGuarantor = (guarantorId: string) => {
    setFormData(prev => ({
      ...prev,
      guarantors: prev.guarantors.filter(g => g.id !== guarantorId)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.amount || !formData.purpose || !formData.monthlyIncome) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.guarantors.length < 2) {
      alert('Please select at least 2 guarantors for your loan application');
      return;
    }

    setLoading(true);
    try {
      const loanData = {
        chamaId,
        amount: parseFloat(formData.amount),
        purpose: formData.purpose,
        repaymentPeriod: parseInt(formData.repaymentPeriod),
        interestRate: parseFloat(formData.interestRate),
        guarantors: formData.guarantors.map(g => g.id),
        businessPlan: formData.businessPlan,
        monthlyIncome: parseFloat(formData.monthlyIncome),
        otherLoans: formData.otherLoans,
      };

      await onSubmit(loanData);

      // Reset form
      setFormData({
        amount: '',
        purpose: '',
        monthlyIncome: '',
        repaymentPeriod: '12',
        interestRate: '5',
        businessPlan: '',
        otherLoans: '',
        guarantors: [],
      });
      onClose();
    } catch (error) {
      console.error('Failed to submit loan application:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: spacing.md,
    }}>
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: borderRadius.xl,
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'hidden',
        ...getShadowStyle('lg'),
      }}>
        {/* Modal Header */}
        <div style={{
          padding: spacing.lg,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{
            margin: 0,
            color: colors.text,
            fontSize: typography.fontSize.xl,
            fontWeight: typography.fontWeight.bold,
          }}>
            Apply for Loan
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textSecondary,
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div style={{
          padding: spacing.lg,
          overflowY: 'auto',
          maxHeight: 'calc(90vh - 140px)',
        }}>
          {/* Loan Amount */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              color: colors.text,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              marginBottom: spacing.xs,
            }}>
              Loan Amount (KES) *
            </label>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="Enter loan amount"
              style={{
                width: '100%',
                padding: spacing.md,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: typography.fontSize.base,
              }}
            />
          </div>

          {/* Purpose */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              color: colors.text,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              marginBottom: spacing.xs,
            }}>
              Purpose *
            </label>
            <textarea
              value={formData.purpose}
              onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
              placeholder="What will you use this loan for?"
              rows={3}
              style={{
                width: '100%',
                padding: spacing.md,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: typography.fontSize.base,
                resize: 'vertical',
              }}
            />
          </div>

          {/* Monthly Income */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              color: colors.text,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              marginBottom: spacing.xs,
            }}>
              Monthly Income (KES) *
            </label>
            <input
              type="number"
              value={formData.monthlyIncome}
              onChange={(e) => setFormData(prev => ({ ...prev, monthlyIncome: e.target.value }))}
              placeholder="Your monthly income"
              style={{
                width: '100%',
                padding: spacing.md,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: typography.fontSize.base,
              }}
            />
          </div>

          {/* Repayment Period */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              color: colors.text,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              marginBottom: spacing.xs,
            }}>
              Repayment Period (Months)
            </label>
            <input
              type="number"
              value={formData.repaymentPeriod}
              onChange={(e) => setFormData(prev => ({ ...prev, repaymentPeriod: e.target.value }))}
              placeholder="12"
              style={{
                width: '100%',
                padding: spacing.md,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: typography.fontSize.base,
              }}
            />
          </div>

          {/* Interest Rate */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              color: colors.text,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              marginBottom: spacing.xs,
            }}>
              Interest Rate (%)
            </label>
            <input
              type="number"
              value={formData.interestRate}
              onChange={(e) => setFormData(prev => ({ ...prev, interestRate: e.target.value }))}
              placeholder="5"
              style={{
                width: '100%',
                padding: spacing.md,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: typography.fontSize.base,
              }}
            />
          </div>

          {/* Business Plan */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              color: colors.text,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              marginBottom: spacing.xs,
            }}>
              Business Plan (Optional)
            </label>
            <textarea
              value={formData.businessPlan}
              onChange={(e) => setFormData(prev => ({ ...prev, businessPlan: e.target.value }))}
              placeholder="Describe your business plan..."
              rows={4}
              style={{
                width: '100%',
                padding: spacing.md,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: typography.fontSize.base,
                resize: 'vertical',
              }}
            />
          </div>

          {/* Other Loans */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              color: colors.text,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              marginBottom: spacing.xs,
            }}>
              Other Loans (Optional)
            </label>
            <textarea
              value={formData.otherLoans}
              onChange={(e) => setFormData(prev => ({ ...prev, otherLoans: e.target.value }))}
              placeholder="Do you have any other loans?"
              rows={2}
              style={{
                width: '100%',
                padding: spacing.md,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: typography.fontSize.base,
                resize: 'vertical',
              }}
            />
          </div>

          {/* Guarantors Section */}
          <div style={{ marginBottom: spacing.lg }}>
            <h3 style={{
              color: colors.text,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              marginBottom: spacing.xs,
            }}>
              Guarantors ({formData.guarantors.length}/5) *
            </h3>
            <p style={{
              color: colors.textSecondary,
              fontSize: typography.fontSize.sm,
              marginBottom: spacing.md,
            }}>
              Select at least 2 guarantors for your loan
            </p>

            {/* Selected Guarantors */}
            {formData.guarantors.map((guarantor) => (
              <div key={guarantor.id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: spacing.md,
                backgroundColor: colors.backgroundSecondary,
                borderRadius: borderRadius.md,
                marginBottom: spacing.sm,
              }}>
                <div>
                  <div style={{
                    color: colors.text,
                    fontSize: typography.fontSize.base,
                    fontWeight: typography.fontWeight.medium,
                  }}>
                    {guarantor.firstName} {guarantor.lastName}
                  </div>
                  <div style={{
                    color: colors.textSecondary,
                    fontSize: typography.fontSize.sm,
                  }}>
                    {guarantor.email}
                  </div>
                </div>
                <button
                  onClick={() => removeGuarantor(guarantor.id)}
                  style={{
                    backgroundColor: colors.error,
                    border: 'none',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              </div>
            ))}

            {/* Add Guarantor Button */}
            {formData.guarantors.length < 5 && (
              <button
                onClick={() => setShowGuarantorSearch(true)}
                style={{
                  width: '100%',
                  padding: spacing.md,
                  border: `2px dashed ${colors.primary}`,
                  borderRadius: borderRadius.md,
                  backgroundColor: 'transparent',
                  color: colors.primary,
                  fontSize: typography.fontSize.base,
                  fontWeight: typography.fontWeight.medium,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: spacing.sm,
                }}
              >
                <span style={{ fontSize: '20px' }}>+</span>
                Add Guarantor
              </button>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              width: '100%',
              padding: spacing.md,
              backgroundColor: loading ? colors.textSecondary : colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: borderRadius.lg,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.medium,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.sm,
            }}
          >
            {loading ? 'Submitting...' : 'Submit Application'}
            {!loading && <span>📤</span>}
          </button>
        </div>
      </div>

      {/* Guarantor Search Modal */}
      {showGuarantorSearch && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: spacing.md,
        }}>
          <div style={{
            backgroundColor: colors.surface,
            borderRadius: borderRadius.xl,
            width: '100%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'hidden',
            ...getShadowStyle('lg'),
          }}>
            <div style={{
              padding: spacing.lg,
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h3 style={{
                margin: 0,
                color: colors.text,
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.bold,
              }}>
                Select Guarantor
              </h3>
              <button
                onClick={() => setShowGuarantorSearch(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textSecondary,
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: spacing.lg }}>
              <input
                type="text"
                value={guarantorSearch}
                onChange={(e) => setGuarantorSearch(e.target.value)}
                placeholder="Search by name, email, or phone..."
                style={{
                  width: '100%',
                  padding: spacing.md,
                  border: `1px solid ${colors.border}`,
                  borderRadius: borderRadius.md,
                  backgroundColor: colors.background,
                  color: colors.text,
                  fontSize: typography.fontSize.base,
                  marginBottom: spacing.md,
                }}
              />

              <div style={{
                maxHeight: '300px',
                overflowY: 'auto',
              }}>
                {availableGuarantors.length > 0 ? (
                  availableGuarantors.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => addGuarantor(user)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: spacing.md,
                        borderBottom: `1px solid ${colors.border}`,
                        cursor: 'pointer',
                      }}
                    >
                      <div>
                        <div style={{
                          color: colors.text,
                          fontSize: typography.fontSize.base,
                          fontWeight: typography.fontWeight.medium,
                        }}>
                          {user.firstName} {user.lastName}
                        </div>
                        <div style={{
                          color: colors.textSecondary,
                          fontSize: typography.fontSize.sm,
                        }}>
                          {user.email}
                        </div>
                      </div>
                      <span style={{
                        color: colors.primary,
                        fontSize: '20px',
                      }}>
                        +
                      </span>
                    </div>
                  ))
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: spacing.xl,
                    color: colors.textSecondary,
                  }}>
                    No users found. Try a different search term.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface Loan {
  id: string;
  amount: number;
  interestRate: number;
  term: number;
  status: 'pending' | 'approved' | 'active' | 'paid' | 'overdue';
  borrower: {
    id: string;
    name: string;
    avatar?: string;
  };
  createdAt: string;
  dueDate?: string;
  remainingBalance?: number;
}

const ChamaLoans: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const colors = getThemeColors('dark');
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [userRole, setUserRole] = useState('member');
  const [showLoanForm, setShowLoanForm] = useState(false);

  useEffect(() => {
    if (chamaId) {
      loadLoans();
      loadUserRole();
    }
  }, [chamaId]);

  const loadUserRole = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      // Get user ID from JWT token
      const tokenPayload = JSON.parse(atob(authToken.split('.')[1]));
      const userId = tokenPayload.userId;

      const response = await fetch(`${API_BASE_URL}/api/v1/chamas/${chamaId}/members/${userId}/role`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUserRole(data.data?.role || 'member');
        }
      }
    } catch (error) {
      console.error('Failed to load user role:', error);
      setUserRole('member');
    }
  };

  const loadLoans = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ ChamaLoans: No auth token found');
        alert('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/loans/?chamaId=${chamaId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        // Add timeout to prevent hanging requests
        signal: AbortSignal.timeout(10000), // 10 second timeout
        // CORS mode for development
        mode: 'cors',
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ ChamaLoans: Failed to load loans - Status:', response.status, 'Error:', errorText);

        if (response.status === 404) {
          alert('Loans endpoint not found. The backend may not be running or the route is incorrect.');
        } else if (response.status === 401) {
          alert('Authentication failed. Please log in again.');
          localStorage.removeItem('authToken');
          // Redirect to login if needed
        } else if (response.status === 403) {
          alert('Access denied. You may not have permission to view loans for this chama.');
        } else {
          alert(`Failed to load loans: ${response.status} - ${errorText}`);
        }
        setLoans([]);
        return;
      }

      const data = await response.json();

      if (data.success && data.data) {
        // Map backend data to frontend interface
        const mappedLoans = data.data.map((loan: any) => ({
          id: loan.id,
          amount: loan.amount,
          interestRate: loan.interestRate,
          term: loan.duration,
          status: loan.status,
          borrower: {
            id: loan.borrower.id,
            name: loan.borrower.fullName,
            avatar: undefined, // not provided by backend
          },
          createdAt: loan.createdAt,
          dueDate: loan.dueDate,
          remainingBalance: loan.remainingAmount,
        }));
        setLoans(mappedLoans);
      } else {
        console.warn('⚠️ ChamaLoans: API returned success but no data:', data);
        setLoans([]);
      }
    } catch (error) {
      console.error('❌ ChamaLoans: Error loading loans:', error);

      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        alert('Network error: Unable to connect to the server. Please check:\n1. Backend server is running on port 8080\n2. No firewall blocking the connection\n3. CORS configuration');
      } else if (error instanceof DOMException && error.name === 'TimeoutError') {
        alert('Request timed out. The server may be overloaded or unreachable.');
      } else {
        alert('An unexpected error occurred while loading loans.');
      }
      setLoans([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleApplyForLoan = () => {
    setShowLoanForm(true);
  };

  const handleLoanApplicationSubmit = async (loanData: any) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        alert('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/loans/apply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loanData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to submit loan application: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      if (data.success) {
        alert('Loan application submitted successfully! Guarantors will be notified.');
        await loadLoans(); // Refresh the loans list
      } else {
        throw new Error(data.error || 'Failed to submit loan application');
      }
    } catch (error) {
      console.error('Failed to submit loan application:', error);
      alert(error instanceof Error ? error.message : 'Failed to submit loan application');
    }
  };

  const handleLoanAction = async (loan: Loan, action: string) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        alert('Authentication required. Please log in again.');
        return;
      }

      let endpoint = '';
      let method = 'POST';
      let body: any = {};

      switch (action) {
        case 'approve':
          endpoint = `${API_BASE_URL}/api/v1/loans/${loan.id}/approve`;
          body = { approved: true };
          break;
        case 'reject':
          endpoint = `${API_BASE_URL}/api/v1/loans/${loan.id}/reject`;
          body = { reason: 'Loan application rejected by reviewer' };
          break;
        case 'guarantee':
          endpoint = `${API_BASE_URL}/api/v1/loans/${loan.id}/guarantee`;
          body = { accepted: true };
          break;
        case 'guarantee_reject':
          endpoint = `${API_BASE_URL}/api/v1/loans/${loan.id}/guarantee`;
          body = { accepted: false };
          break;
        default:
          return;
      }

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to ${action} loan: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      if (data.success) {
        alert(`Loan ${action.replace('_', ' ')} successful!`);
        await loadLoans(); // Refresh the loans list
      } else {
        throw new Error(data.error || `Failed to ${action} loan`);
      }
    } catch (error) {
      console.error(`Failed to ${action} loan:`, error);
      alert(error instanceof Error ? error.message : `Failed to ${action} loan`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return colors.warning;
      case 'approved': return colors.info;
      case 'active': return colors.primary;
      case 'paid': return colors.success;
      case 'overdue': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'approved': return '✅';
      case 'active': return '💳';
      case 'paid': return '🎉';
      case 'overdue': return '⚠️';
      default: return '❓';
    }
  };

  const filteredLoans = loans.filter(loan => {
    if (activeFilter === 'all') return true;
    return loan.status === activeFilter;
  });

  const renderLoanCard = (loan: Loan) => (
    <div key={loan.id} style={{ ...styles.loanCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
      <div style={styles.loanHeader}>
        <div style={styles.borrowerInfo}>
          <div style={styles.borrowerAvatar}>
            {loan.borrower.avatar ? (
              <img src={loan.borrower.avatar} alt={loan.borrower.name} style={styles.avatarImage} />
            ) : (
              <span style={{ fontSize: '16px' }}>
                {loan.borrower.name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </span>
            )}
          </div>
          <div style={styles.borrowerDetails}>
            <div style={{ ...styles.borrowerName, color: colors.text }}>{loan.borrower.name}</div>
            <div style={{ ...styles.loanDate, color: colors.textSecondary }}>
              {new Date(loan.createdAt).toLocaleDateString()}
            </div>
          </div>
        </div>
        <div style={{ ...styles.loanStatus, backgroundColor: getStatusColor(loan.status) + '20', border: `1px solid ${getStatusColor(loan.status)}` }}>
          <span style={{ fontSize: '16px', marginRight: '4px' }}>{getStatusIcon(loan.status)}</span>
          <span style={{ ...styles.statusText, color: getStatusColor(loan.status) }}>
            {loan.status.charAt(0).toUpperCase() + loan.status.slice(1)}
          </span>
        </div>
      </div>

      <div style={styles.loanDetails}>
        <div style={styles.loanAmount}>
          <div style={{ ...styles.amountLabel, color: colors.textSecondary }}>Loan Amount</div>
          <div style={{ ...styles.amountValue, color: colors.text }}>{formatCurrency(loan.amount)}</div>
        </div>

        <div style={styles.loanTerms}>
          <div style={styles.termItem}>
            <div style={{ ...styles.termLabel, color: colors.textSecondary }}>Interest Rate</div>
            <div style={{ ...styles.termValue, color: colors.text }}>{loan.interestRate}%</div>
          </div>
          <div style={styles.termItem}>
            <div style={{ ...styles.termLabel, color: colors.textSecondary }}>Term</div>
            <div style={{ ...styles.termValue, color: colors.text }}>{loan.term} months</div>
          </div>
        </div>

        {loan.remainingBalance !== undefined && (
          <div style={styles.remainingBalance}>
            <div style={{ ...styles.balanceLabel, color: colors.textSecondary }}>Remaining Balance</div>
            <div style={{ ...styles.balanceValue, color: colors.text }}>{formatCurrency(loan.remainingBalance)}</div>
          </div>
        )}

        {loan.dueDate && (
          <div style={styles.dueDate}>
            <div style={{ ...styles.dueLabel, color: colors.textSecondary }}>Due Date</div>
            <div style={{ ...styles.dueValue, color: loan.status === 'overdue' ? colors.error : colors.text }}>
              {new Date(loan.dueDate).toLocaleDateString()}
            </div>
          </div>
        )}
      </div>

      <div style={styles.loanActions}>
      
        {loan.status === 'approved' && (
          <>
            <button
              style={{ ...styles.actionButton, backgroundColor: colors.primary }}
              onClick={() => handleLoanAction(loan, 'guarantee')}
            >
              Accept
            </button>
            <button
              style={{ ...styles.actionButton, backgroundColor: colors.error }}
              onClick={() => handleLoanAction(loan, 'guarantee_reject')}
            >
              Reject
            </button>
          </>
        )}
        {loan.status === 'active' && (
          <button style={{ ...styles.actionButton, backgroundColor: colors.success }}>
            Make Payment
          </button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading loans...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Chama Loans"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Summary Cards */}
          <div style={styles.summaryGrid}>
            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>💰</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {formatCurrency(loans.reduce((sum, loan) => sum + loan.amount, 0))}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Loan Amount</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>✅</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {loans.filter(loan => loan.status === 'active').length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Active Loans</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>⏳</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {loans.filter(loan => loan.status === 'pending').length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Pending Applications</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>⚠️</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {loans.filter(loan => loan.status === 'overdue').length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Overdue Loans</div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div style={styles.tabContainer}>
            {[
              { id: 'all', label: 'All Loans', count: loans.length },
              { id: 'active', label: 'Active', count: loans.filter(l => l.status === 'active').length },
              { id: 'pending', label: 'Pending', count: loans.filter(l => l.status === 'pending').length },
              { id: 'paid', label: 'Paid', count: loans.filter(l => l.status === 'paid').length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                style={{
                  ...styles.tabButton,
                  backgroundColor: activeFilter === tab.id ? colors.primary : 'transparent',
                  color: activeFilter === tab.id ? 'white' : colors.text,
                }}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Loans List */}
          <div style={styles.loansList}>
            {filteredLoans.length > 0 ? (
              filteredLoans.map(renderLoanCard)
            ) : (
              <div style={{ ...styles.emptyState, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                <div style={styles.emptyIcon}>💳</div>
                <div style={{ ...styles.emptyTitle, color: colors.text }}>No loans found</div>
                <div style={{ ...styles.emptySubtitle, color: colors.textSecondary }}>
                  {activeFilter === 'all'
                    ? 'No loans have been issued in this chama yet.'
                    : `No ${activeFilter} loans found.`
                  }
                </div>
              </div>
            )}
          </div>

          {/* Apply for Loan Button */}
          <button
            onClick={handleApplyForLoan}
            style={{ ...styles.applyButton, backgroundColor: colors.primary }}
          >
            Apply for Loan
          </button>
        </div>
      </div>

      {/* Loan Application Form Modal */}
      <LoanApplicationForm
        isVisible={showLoanForm}
        onClose={() => setShowLoanForm(false)}
        onSubmit={handleLoanApplicationSubmit}
        chamaId={chamaId!}
      />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  summaryCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    display: 'flex',
    alignItems: 'center',
    ...getShadowStyle('sm'),
  },
  summaryIcon: {
    fontSize: '24px',
    marginRight: spacing.md,
  },
  summaryInfo: {
    flex: 1,
  },
  summaryValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
  },
  summaryLabel: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  tabContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  tabButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.md,
    border: 'none',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  loansList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  loanCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    ...getShadowStyle('sm'),
  },
  loanHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  borrowerInfo: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  borrowerAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 20,
    objectFit: 'cover',
  },
  borrowerDetails: {
    flex: 1,
  },
  borrowerName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  loanDate: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  loanStatus: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.md,
    display: 'flex',
    alignItems: 'center',
  },
  statusText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  loanDetails: {
    marginBottom: spacing.md,
  },
  loanAmount: {
    marginBottom: spacing.md,
  },
  amountLabel: {
    fontSize: typography.fontSize.sm,
  },
  amountValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    marginTop: spacing.xs / 2,
  },
  loanTerms: {
    display: 'flex',
    gap: spacing.lg,
    marginBottom: spacing.md,
  },
  termItem: {
    flex: 1,
  },
  termLabel: {
    fontSize: typography.fontSize.sm,
  },
  termValue: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.xs / 2,
  },
  remainingBalance: {
    marginBottom: spacing.md,
  },
  balanceLabel: {
    fontSize: typography.fontSize.sm,
  },
  balanceValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    marginTop: spacing.xs / 2,
  },
  dueDate: {
    marginBottom: spacing.md,
  },
  dueLabel: {
    fontSize: typography.fontSize.sm,
  },
  dueValue: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.xs / 2,
  },
  loanActions: {
    display: 'flex',
    gap: spacing.sm,
  },
  actionButton: {
    flex: 1,
    padding: spacing.sm,
    borderRadius: borderRadius.md,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: spacing.xl,
    borderRadius: borderRadius.lg,
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: spacing.md,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    fontSize: typography.fontSize.base,
  },
  applyButton: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    marginTop: spacing.lg,
  },
};

export default ChamaLoans;